import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/me/report-styles/index.tsx
 * @description 個人報告樣式模板列表頁（M2）。
 *
 * 功能：
 * - 列出當前用戶的報告樣式模板（Card 列表）
 * - 顯示名稱/描述/範例數/是否預設
 * - 建立新模板按鈕
 * - 設為預設 / 刪除操作
 *
 * 視覺：PageShell + SectionCard + Card 列表
 *
 * 角色：
 * - 需要 report-style.manage 能力（auditor / manager）
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge, Button, Card, Col, Popconfirm, Row, Space, Tag } from 'antd';
import { message } from '@/lib/antd-app';
import { Plus, Star, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { PageShell, EmptyState } from '@/components/shell';
import { space } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/me/report-styles/')({
  component: ReportStylesListPage,
});

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface ReportStyleTemplate {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
  exampleReportIds: number[] | null;
  promptDirectives: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 個人報告樣式模板列表頁面。
 */
function ReportStylesListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  /** 查詢模板列表 */
  const { data, isLoading } = useQuery<{ items: ReportStyleTemplate[] }>({
    queryKey: ['report-styles'],
    queryFn: () => apiClient.get('/api/me/report-styles'),
  });

  const templates = data?.items ?? [];

  /** 設為預設 */
  const setDefaultMutation = useMutation({
    mutationFn: (id: number) => apiClient.patch(`/api/me/report-styles/${id}/set-default`, {}),
    onSuccess: () => {
      message.success('已設為預設模板');
      void qc.invalidateQueries({ queryKey: ['report-styles'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  /** 軟刪除 */
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/me/report-styles/${id}`),
    onSuccess: () => {
      message.success('模板已刪除');
      void qc.invalidateQueries({ queryKey: ['report-styles'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <PageShell
      title="報告樣式模板"
      description="管理您的 AI 報告生成樣式偏好，設定提示詞與範例報告"
      actions={
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={() => void navigate({ to: '/me/report-styles/$id', params: { id: 'new' } })}
        >
          建立模板
        </Button>
      }
    >
      {/* 模板卡片列表 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: space[8] }}>載入中...</div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<Plus size={32} />}
          title="尚無報告樣式模板"
          description="建立第一個模板，設定 AI 報告生成的樣式偏好"
          action={
            <Button
              type="primary"
              onClick={() => void navigate({ to: '/me/report-styles/$id', params: { id: 'new' } })}
            >
              建立第一個模板
            </Button>
          }
        />
      ) : (
        <Row gutter={[16, 16]}>
          {templates.map((t) => (
            <Col key={t.id} xs={24} sm={12} lg={8}>
              <Card
                hoverable
                onClick={() =>
                  void navigate({ to: '/me/report-styles/$id', params: { id: String(t.id) } })
                }
                actions={[
                  <Button
                    key="default"
                    type={t.isDefault ? 'primary' : 'text'}
                    size="small"
                    icon={<Star size={12} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!t.isDefault) setDefaultMutation.mutate(t.id);
                    }}
                    disabled={t.isDefault}
                  >
                    {t.isDefault ? '預設' : '設為預設'}
                  </Button>,
                  <Popconfirm
                    key="delete"
                    title="確定刪除此模板？"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      deleteMutation.mutate(t.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="刪除"
                    cancelText="取消"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={12} />}
                      onClick={(e) => e.stopPropagation()}
                    >
                      刪除
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  title={
                    <Space>
                      <span>{t.name}</span>
                      {t.isDefault && <Badge status="success" text="預設" />}
                    </Space>
                  }
                  description={
                    <div>
                      <div className="text-sm text-gray-500 mb-2 line-clamp-2">
                        {t.description ?? '暫無描述'}
                      </div>
                      <Space>
                        {t.exampleReportIds && t.exampleReportIds.length > 0 && (
                          <Tag color="blue">{t.exampleReportIds.length} 個範例報告</Tag>
                        )}
                        {t.promptDirectives && <Tag color="purple">有提示詞</Tag>}
                      </Space>
                      <div className="text-xs text-gray-300 mt-2">
                        更新：{formatDate(t.updatedAt)}
                      </div>
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </PageShell>
  );
}
